import { useEffect, useMemo, useState } from "react";
import {
    FaArrowLeft,
    FaCircleCheck,
    FaCircleInfo,
    FaCircleXmark,
    FaUser,
    FaMoneyBill,
    FaQrcode,
    FaBuildingColumns,
    FaBolt,
    FaLock,
} from "react-icons/fa6";
import { FaCalendarAlt } from "react-icons/fa";
import { formatRupiah } from "../../../../utilities/formatRupiah";
import { ModalWraper } from "../../../../components/modal/wraper";
import { SelectPppoeUser } from "../../../../components/forms/selects/SelectPppoeUser";
import { InvoiceService } from "../../../../services/InvoiceService";

const MONTH_NAMES = [
    "Januari", "Februari", "Maret", "April", "Mei", "Juni",
    "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

const PAYMENT_METHODS = [
    { value: "cash", label: "Cash", icon: FaMoneyBill, desc: "Bayar tunai" },
    { value: "qris", label: "QRIS", icon: FaQrcode, desc: "Scan QR code" },
    { value: "transfer", label: "Transfer", icon: FaBuildingColumns, desc: "Transfer bank" },
];

/* ── Helpers ── */
const getPeriodKey = (inv) => {
    if (inv.period) return inv.period;
    if (inv.periode) return inv.periode;
    if (inv.start_periode) {
        const d = new Date(inv.start_periode);
        if (!isNaN(d.getTime())) return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    }
    return null;
};

const getPeriodLabel = (key) => {
    if (!key) return "-";
    const [y, m] = key.split("-");
    return `${MONTH_NAMES[parseInt(m) - 1]} ${y}`;
};

const isUnpaid = (status) => {
    if (!status) return true;
    return status.toLowerCase().replace(/[_\s-]/g, "") === "belumbayar";
};

const getPaymentBadge = (status) => {
    if (isUnpaid(status))
        return { color: "#ef4444", bg: "rgba(239,68,68,0.1)", border: "rgba(239,68,68,0.2)", label: "Belum Dibayar", icon: FaCircleXmark };
    if (status?.toLowerCase().replace(/[_\s-]/g, "") === "dibayar_sebagian" || status?.toLowerCase().replace(/[_\s-]/g, "") === "sebagian")
        return { color: "#f59e0b", bg: "rgba(245,158,11,0.1)", border: "rgba(245,158,11,0.2)", label: "Sebagian", icon: FaCircleInfo };
    return { color: "#10b981", bg: "rgba(16,185,129,0.1)", border: "rgba(16,185,129,0.2)", label: "Lunas", icon: FaCircleCheck };
};

export const ModalInvoiceCreate = ({
    refresh,
    openModal,
    setOpenModal,
    nominalPerPeriode: defaultNominal = 0,
}) => {
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [errors, setErrors] = useState({});
    const [userInvoices, setUserInvoices] = useState([]);

    const [formData, setFormData] = useState({
        user: null,
        start_periode: "",
        due_date: "",
        discount: "",
        ppn: "",
        nominal_bayar: "",
        metode_bayar: "",
    });

    /* ---- Harga per bulan penuh ---- */
    const nominalPerBulan = formData.user?.profile?.price
        ? parseFloat(formData.user.profile.price)
        : defaultNominal || 0;

    /* ══════════════════════════════════════════
       PRORATA
       ══════════════════════════════════════════ */
    const proratedInfo = useMemo(() => {
        if (!formData.start_periode) return null;
        const d = new Date(formData.start_periode);
        if (isNaN(d.getTime())) return null;

        const year = d.getFullYear();
        const month = d.getMonth();
        const startDay = d.getDate();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const daysInPeriod = daysInMonth - startDay + 1;
        const isFullMonth = startDay === 1;
        const dailyRate = daysInMonth > 0 ? nominalPerBulan / daysInMonth : 0;
        const proratedPrice = isFullMonth
            ? nominalPerBulan
            : Math.round(dailyRate * daysInPeriod);

        return { daysInMonth, daysInPeriod, startDay, isFullMonth, dailyRate, proratedPrice };
    }, [formData.start_periode, nominalPerBulan]);

    const nominalPeriode = proratedInfo?.proratedPrice ?? nominalPerBulan;

    /* ---- Fetch invoice user via show endpoint ---- */
    useEffect(() => {
        const fetchInvoices = async () => {
            if (formData?.user?.id) {
                try {
                    const res = await InvoiceService.show({ id: formData.user.id });
                    // InvoiceDetailResource biasanya punya data invoices di dalamnya
                    const invoices = res.data?.data?.invoices
                        || res.data?.invoices
                        || (Array.isArray(res.data?.data) ? res.data.data : [])
                        || [];
                    setUserInvoices(invoices);
                } catch {
                    setUserInvoices([]);
                }
            } else {
                setUserInvoices([]);
            }
        };
        fetchInvoices();
    }, [formData.user?.id]);

    /* ---- Periode ---- */
    const periodInfo = useMemo(() => {
        if (!formData.start_periode) return null;
        const d = new Date(formData.start_periode);
        if (isNaN(d.getTime())) return null;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        return { key, label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}` };
    }, [formData.start_periode]);

    /* ---- Validasi periode ---- */
    const periodValidation = useMemo(() => {
        if (!periodInfo || !formData.user?.id) return null;

        const invoiceMap = {};
        userInvoices.forEach((inv) => {
            const key = getPeriodKey(inv);
            if (key) invoiceMap[key] = inv;
        });

        const existing = invoiceMap[periodInfo.key];
        if (existing) {
            const badge = getPaymentBadge(existing.status);
            return { canIssue: false, reason: "exists", invoice: existing, badge };
        }

        const [y, m] = periodInfo.key.split("-").map(Number);
        const prevY = m === 1 ? y - 1 : y;
        const prevM = m === 1 ? 12 : m - 1;
        const prevKey = `${prevY}-${String(prevM).padStart(2, "0")}`;
        const prevInv = invoiceMap[prevKey];

        if (prevInv && isUnpaid(prevInv.status)) {
            return { canIssue: false, reason: "blocked", blockedByPeriod: prevKey };
        }

        return { canIssue: true };
    }, [periodInfo, formData.user?.id, userInvoices]);

    const sortedInvoices = useMemo(() => {
        if (!userInvoices.length) return [];
        return [...userInvoices].sort((a, b) => {
            const ka = getPeriodKey(a) || "";
            const kb = getPeriodKey(b) || "";
            return ka.localeCompare(kb);
        });
    }, [userInvoices]);

    /* ---- Kalkulasi ---- */
    const calc = useMemo(() => {
        const subtotal = nominalPeriode;
        const discount = Math.max(0, Number(formData.discount) || 0);
        const afterDiscount = Math.max(0, subtotal - discount);
        const ppnPercent = Math.max(0, Number(formData.ppn) || 0);
        const ppnAmount = afterDiscount * (ppnPercent / 100);
        const grandTotal = afterDiscount + ppnAmount;
        return { subtotal, discount, afterDiscount, ppnPercent, ppnAmount, grandTotal };
    }, [nominalPeriode, formData.discount, formData.ppn]);

    const sisaPembayaran = useMemo(() => {
        return (Number(formData.nominal_bayar) || 0) - calc.grandTotal;
    }, [formData.nominal_bayar, calc.grandTotal]);

    /* ---- Reset ---- */
    const closeModal = () => {
        setOpenModal(false);
        setFormData({ user: null, start_periode: "", due_date: "", discount: "", ppn: "", nominal_bayar: "", metode_bayar: "" });
        setStep(1);
        setUserInvoices([]);
        setLoading(false);
        setErrors({});
    };

    /* ---- Handlers ---- */
    const handleUserChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            user: value,
            start_periode: value?.profile?.start_periode || "",
            due_date: "",
            discount: "",
            ppn: "",
            nominal_bayar: "",
            metode_bayar: "",
        }));
        setErrors({});
    };

    /* ══════════════════════════════════════════
       Error handler
       ══════════════════════════════════════════ */
    const handleError = (err, context = "Gagal menerbitkan invoice") => {
        console.error(`${context}:`, err);

        const serverErrors = err?.response?.data;

        if (typeof serverErrors === "object" && serverErrors !== null) {
            const formatted = {};
            for (const [key, value] of Object.entries(serverErrors)) {
                if (key === "message" || key === "message_v2" || key === "status") continue;
                formatted[key] = Array.isArray(value) ? value[0] : value;
            }
            if (Object.keys(formatted).length > 0) {
                setErrors(formatted);
            } else if (serverErrors.message_v2) {
                setErrors({ general: serverErrors.message_v2 });
            } else if (serverErrors.message) {
                setErrors({ general: serverErrors.message });
            } else {
                setErrors({ general: context });
            }
        } else if (serverErrors?.message) {
            setErrors({ general: serverErrors.message });
        } else {
            setErrors({ general: context });
        }
    };

    const validateBase = () => {
        setErrors({});
        const errs = {};

        if (!formData.user?.id) errs.user = "Silakan pilih pelanggan.";
        if (!formData.start_periode) errs.start_periode = "Tanggal mulai billing wajib diisi.";
        if (!formData.due_date) errs.due_date = "Tanggal jatuh tempo wajib diisi.";

        if (!periodValidation?.canIssue && periodValidation?.reason) {
            if (periodValidation.reason === "exists") errs.period = "Periode ini sudah memiliki invoice.";
            else if (periodValidation.reason === "blocked") errs.period = "Periode sebelumnya belum dibayar.";
            else errs.period = "Periode tidak valid.";
        }

        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return false;
        }
        return true;
    };

    /* ══════════════════════════════════════════
       Payload builder — hanya field yang ada di DB
       Frontend hitung semua (prorata, diskon, ppn),
       tapi yang dikirim cuma hasil akhirnya.
       ══════════════════════════════════════════ */
    const buildPayload = (withPayment = false) => {
        const payload = {
            pppoe_user_id: formData.user.id,
            start_periode: formData.start_periode,
            due_date: formData.due_date,
            nominal_per_period: calc.grandTotal,
        };

        if (withPayment) {
            payload.nominal_bayar = Number(formData.nominal_bayar);
            payload.metode_bayar = formData.metode_bayar;
        }

        return payload;
    };

    /* ---- Jalur 1: Terbitkan langsung ---- */
    const handleDirectPublish = async () => {
        if (!validateBase()) return;

        setLoading(true);
        setErrors({});

        try {
            await InvoiceService.create({ data: buildPayload(false) });

            if (refresh) refresh();
            closeModal();
        } catch (err) {
            handleError(err, "Gagal menerbitkan invoice.");
        } finally {
            setLoading(false);
        }
    };

    /* ---- Jalur 2: Terbitkan + bayar ---- */
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateBase()) return;

        const errs = {};
        if (!formData.metode_bayar) errs.metode_bayar = "Pilih metode pembayaran.";
        if (!formData.nominal_bayar || Number(formData.nominal_bayar) <= 0) errs.nominal_bayar = "Nominal pembayaran harus diisi.";

        if (Object.keys(errs).length > 0) {
            setErrors((prev) => ({ ...prev, ...errs }));
            return;
        }

        setLoading(true);
        setErrors({});

        try {
            await InvoiceService.create({ data: buildPayload(true) });

            if (refresh) refresh();
            closeModal();
        } catch (err) {
            handleError(err, "Gagal menerbitkan invoice dengan pembayaran.");
        } finally {
            setLoading(false);
        }
    };

    const subtitle = step === 1 ? "Atur periode dan terbitkan invoice" : "Atur pembayaran dan terbitkan invoice";
    const canAction = formData.user && periodValidation?.canIssue && formData.due_date && !loading;

    /* ══════════════════════════════════
       Error UI
       ══════════════════════════════════ */
    const ErrorBanner = ({ message }) => (
        <div
            className="rounded-xl p-4 flex items-start gap-3"
            style={{ background: "rgba(239, 68, 68, 0.06)", border: "1px solid rgba(239, 68, 68, 0.2)" }}
        >
            <FaCircleXmark className="text-red-400 text-sm mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-400 leading-relaxed">{message}</p>
        </div>
    );

    const FieldError = ({ field }) => {
        if (!errors[field]) return null;
        return <p className="text-[11px] text-red-400 mt-1.5">{errors[field]}</p>;
    };

    /* ══════════════════════════════════
       STEP 1
       ══════════════════════════════════ */
    const renderStep1 = () => (
        <>
            {errors.general && <ErrorBanner message={errors.general} />}

            <div>
                <SelectPppoeUser label="Pilih Pelanggan" value={formData.user?.id} onChange={handleUserChange} />
                <FieldError field="user" />
            </div>

            {formData.user && (
                <>
                    {/* ── Info Pelanggan ── */}
                    <div
                        className="rounded-xl p-4 flex items-center gap-4"
                        style={{
                            background: "rgba(30, 41, 59, 0.5)",
                            border: errors.user
                                ? "1.5px solid rgba(239, 68, 68, 0.4)"
                                : "1px solid rgba(51, 65, 85, 0.3)",
                        }}
                    >
                        <div
                            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: "linear-gradient(135deg, #0077B6, #00B4D8)" }}
                        >
                            <FaUser className="text-white text-sm" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-200 truncate">{formData.user.name || "-"}</p>
                            <p className="text-xs text-slate-500 font-mono">
                                {formData.user.internet_number || "-"} · {formData.user.profile?.name || "-"}
                            </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <p className="text-xs text-slate-500">Harga/Bulan</p>
                            <p className="text-sm font-bold text-cyan-400">Rp {formatRupiah(nominalPerBulan)}</p>
                        </div>
                    </div>

                    {/* ── Info Prorata ── */}
                    {proratedInfo && !proratedInfo.isFullMonth && (
                        <div
                            className="rounded-xl p-4"
                            style={{ background: "rgba(234, 179, 8, 0.06)", border: "1px solid rgba(234, 179, 8, 0.18)" }}
                        >
                            <div className="flex items-start gap-3">
                                <FaCircleInfo className="text-yellow-500 text-sm mt-0.5 flex-shrink-0" />
                                <div className="flex-1 space-y-2">
                                    <p className="text-sm font-semibold text-yellow-400">Periode Tidak Genap 1 Bulan</p>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <div>
                                            <p className="text-[10px] text-yellow-400/60 uppercase tracking-wider">Mulai</p>
                                            <p className="text-sm font-bold text-yellow-300">Tgl {proratedInfo.startDay}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-yellow-400/60 uppercase tracking-wider">Hari Periode</p>
                                            <p className="text-sm font-bold text-yellow-300">{proratedInfo.daysInPeriod} hari</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-yellow-400/60 uppercase tracking-wider">Hari/Bulan</p>
                                            <p className="text-sm font-bold text-yellow-300">{proratedInfo.daysInMonth} hari</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-yellow-400/60 uppercase tracking-wider">Harga/Hari</p>
                                            <p className="text-sm font-bold text-yellow-300">Rp {formatRupiah(Math.round(proratedInfo.dailyRate))}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between pt-2 border-t border-yellow-500/15">
                                        <span className="text-xs text-yellow-400/70">
                                            Rp {formatRupiah(nominalPerBulan)} ÷ {proratedInfo.daysInMonth} hari × {proratedInfo.daysInPeriod} hari
                                        </span>
                                        <span className="text-sm font-extrabold text-yellow-300">Rp {formatRupiah(proratedInfo.proratedPrice)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Start Billing + Due Date ── */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                                <FaCalendarAlt className="inline mr-1.5 text-cyan-400" />
                                Mulai Billing
                            </label>
                            <input
                                type="date"
                                value={formData.start_periode}
                                onChange={(e) => {
                                    setFormData((prev) => ({ ...prev, start_periode: e.target.value }));
                                    setErrors((prev) => { const n = { ...prev }; delete n.start_periode; delete n.period; return n; });
                                }}
                                disabled={loading}
                                className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-4 py-3 text-slate-200 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 disabled:opacity-50 transition"
                                style={errors.start_periode ? { borderColor: "rgba(239,68,68,0.5)" } : {}}
                            />
                            <p className="text-[11px] text-slate-600 mt-1.5">
                                Periode: {periodInfo ? <span className="text-cyan-400 font-semibold">{periodInfo.label}</span> : <span className="text-slate-600">—</span>}
                                {proratedInfo && !proratedInfo.isFullMonth && (
                                    <span className="text-yellow-500/70 ml-2">({proratedInfo.daysInPeriod}/{proratedInfo.daysInMonth} hari)</span>
                                )}
                            </p>
                            <FieldError field="start_periode" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                                <FaCalendarAlt className="inline mr-1.5 text-cyan-400" />
                                Jatuh Tempo
                            </label>
                            <input
                                type="date"
                                value={formData.due_date}
                                onChange={(e) => {
                                    setFormData((prev) => ({ ...prev, due_date: e.target.value }));
                                    setErrors((prev) => { const n = { ...prev }; delete n.due_date; return n; });
                                }}
                                disabled={loading}
                                className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-4 py-3 text-slate-200 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 disabled:opacity-50 transition"
                                style={errors.due_date ? { borderColor: "rgba(239,68,68,0.5)" } : {}}
                            />
                            <FieldError field="due_date" />
                        </div>
                    </div>

                    {errors.period && <ErrorBanner message={errors.period} />}

                    {/* ── Validasi Periode ── */}
                    {periodInfo && periodValidation && !errors.period && (
                        <>
                            {periodValidation.reason === "exists" && (
                                <div
                                    className="rounded-xl p-4 flex items-start gap-3"
                                    style={{ background: "rgba(234, 179, 8, 0.06)", border: "1px solid rgba(234, 179, 8, 0.15)" }}
                                >
                                    <FaCircleInfo className="text-yellow-500 text-sm mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm font-semibold text-yellow-400">
                                            Periode {periodInfo.label} sudah memiliki invoice
                                        </p>
                                        <div className="flex items-center gap-2 mt-1.5">
                                            <span className="text-xs text-yellow-400/70">Status:</span>
                                            <span
                                                className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase"
                                                style={{
                                                    color: periodValidation.badge.color,
                                                    background: periodValidation.badge.bg,
                                                    border: `1px solid ${periodValidation.badge.border}`,
                                                }}
                                            >
                                                {(() => { const I = periodValidation.badge.icon; return <I className="text-[8px]" />; })()}
                                                {periodValidation.badge.label}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {periodValidation.reason === "blocked" && (
                                <div
                                    className="rounded-xl p-4 flex items-start gap-3"
                                    style={{ background: "rgba(239, 68, 68, 0.05)", border: "1px solid rgba(239, 68, 68, 0.15)" }}
                                >
                                    <FaLock className="text-red-400 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm font-semibold text-red-400">
                                            Periode {periodInfo.label} terkunci
                                        </p>
                                        <p className="text-xs text-red-400/70 mt-1 leading-relaxed">
                                            Periode <span className="font-bold text-red-400">{getPeriodLabel(periodValidation.blockedByPeriod)}</span> belum dibayar.
                                            Selesaikan pembayaran terlebih dahulu untuk menerbitkan periode berikutnya.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {periodValidation.canIssue && (
                                <div
                                    className="rounded-xl p-5"
                                    style={{
                                        background: "linear-gradient(135deg, rgba(0, 180, 216, 0.08), rgba(0, 119, 182, 0.04))",
                                        border: "1.5px solid rgba(0, 180, 216, 0.2)",
                                    }}
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-[10px] font-semibold text-cyan-400/50 uppercase tracking-wider mb-1">
                                                Periode yang Akan Diterbitkan
                                            </p>
                                            <p className="text-2xl font-extrabold text-cyan-400">{periodInfo.label}</p>
                                            {proratedInfo && !proratedInfo.isFullMonth && (
                                                <p className="text-[11px] text-cyan-400/50 mt-0.5">
                                                    Proporsional: {proratedInfo.daysInPeriod} dari {proratedInfo.daysInMonth} hari
                                                </p>
                                            )}
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Jatuh Tempo</p>
                                            <p className="text-sm font-bold text-slate-300">{formData.due_date || "—"}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Diskon + PPN ── */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Diskon</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">Rp</span>
                                <input
                                    type="number" min="0" value={formData.discount}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, discount: e.target.value }))}
                                    disabled={loading} placeholder="0"
                                    className="w-full bg-slate-900/80 border border-slate-700 rounded-xl pl-10 pr-4 py-3 text-sm text-slate-200 text-right focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 disabled:opacity-50 transition"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">PPN</label>
                            <div className="relative">
                                <input
                                    type="number" min="0" max="100" value={formData.ppn}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, ppn: e.target.value }))}
                                    disabled={loading} placeholder="0"
                                    className="w-full bg-slate-900/80 border border-slate-700 rounded-xl px-4 pr-10 py-3 text-sm text-slate-200 text-right focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 disabled:opacity-50 transition"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
                            </div>
                        </div>
                    </div>

                    {/* ── Preview Total ── */}
                    {periodValidation?.canIssue && (
                        <div className="rounded-xl p-4 space-y-2" style={{ background: "rgba(0, 180, 216, 0.05)", border: "1px solid rgba(0, 180, 216, 0.12)" }}>
                            {proratedInfo && !proratedInfo.isFullMonth && (
                                <>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-slate-400">Harga/Bulan</span>
                                        <span className="font-semibold text-slate-200">Rp {formatRupiah(nominalPerBulan)}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-yellow-400/80">
                                            Proporsional ({proratedInfo.daysInPeriod}/{proratedInfo.daysInMonth} hari)
                                        </span>
                                        <span className="font-semibold text-yellow-300">
                                            Rp {formatRupiah(proratedInfo.proratedPrice)}
                                        </span>
                                    </div>
                                </>
                            )}
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-400">Subtotal</span>
                                <span className="font-semibold text-slate-200">Rp {formatRupiah(calc.subtotal)}</span>
                            </div>
                            {calc.discount > 0 && (
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-slate-400">Diskon</span>
                                    <span className="font-semibold text-red-400">-Rp {formatRupiah(calc.discount)}</span>
                                </div>
                            )}
                            {calc.ppnAmount > 0 && (
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-slate-400">PPN ({calc.ppnPercent}%)</span>
                                    <span className="font-semibold text-cyan-400">+Rp {formatRupiah(calc.ppnAmount)}</span>
                                </div>
                            )}
                            <div className="border-t border-slate-700/50" />
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-bold text-slate-300 uppercase tracking-wider">Total</span>
                                <span className="text-xl font-extrabold text-cyan-400">Rp {formatRupiah(calc.grandTotal)}</span>
                            </div>
                        </div>
                    )}

                    {/* ── Riwayat Tagihan ── */}
                    {sortedInvoices.length > 0 && (
                        <div className="rounded-xl p-4" style={{ background: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Riwayat Tagihan</p>
                            <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
                                {sortedInvoices.map((inv, idx) => {
                                    const key = getPeriodKey(inv);
                                    const badge = getPaymentBadge(inv.status);
                                    const BIcon = badge.icon;
                                    return (
                                        <div
                                            key={inv.id || key}
                                            className="flex items-center justify-between py-2 px-3 rounded-lg"
                                            style={{ background: idx % 2 === 0 ? "rgba(15, 23, 42, 0.4)" : "transparent" }}
                                        >
                                            <span className="text-sm text-slate-300 font-medium">{getPeriodLabel(key)}</span>
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm font-bold text-slate-400">Rp {formatRupiah(inv.nominal || nominalPerBulan)}</span>
                                                <span
                                                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
                                                    style={{ color: badge.color, background: badge.bg, border: `1px solid ${badge.border}` }}
                                                >
                                                    <BIcon className="text-[9px]" /> {badge.label}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );

    /* ══════════════════════════════════
       STEP 2 — Pembayaran
       ══════════════════════════════════ */
    const renderStep2 = () => (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            {errors.general && (
                <div className="lg:col-span-5">
                    <ErrorBanner message={errors.general} />
                </div>
            )}

            {/* Kiri: Ringkasan */}
            <div className="lg:col-span-3 space-y-4">
                <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, #0077B6, #00B4D8)" }}>
                            <FaUser className="text-white text-sm" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-200 truncate">{formData.user?.name || "-"}</p>
                            <p className="text-xs text-slate-500 font-mono">{formData.user?.internet_number || "-"} · {formData.user?.profile?.name || "-"}</p>
                        </div>
                    </div>
                    <div className="border-t border-slate-700/40" />
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Periode</p>
                            <p className="text-sm font-bold text-cyan-400 mt-0.5">{periodInfo?.label || "-"}</p>
                        </div>
                        <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Mulai Billing</p>
                            <p className="text-sm font-bold text-slate-300 mt-0.5">{formData.start_periode || "-"}</p>
                        </div>
                        <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Jatuh Tempo</p>
                            <p className="text-sm font-bold text-slate-300 mt-0.5">{formData.due_date || "-"}</p>
                        </div>
                    </div>
                    {proratedInfo && !proratedInfo.isFullMonth && (
                        <div className="flex items-center gap-2 text-[11px] text-yellow-400/80">
                            <FaCircleInfo className="text-[10px] flex-shrink-0" />
                            <span>Proporsional: {proratedInfo.daysInPeriod} dari {proratedInfo.daysInMonth} hari ({proratedInfo.startDay} s/d akhir bulan)</span>
                        </div>
                    )}
                </div>

                <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Rincian Harga</p>
                    {proratedInfo && !proratedInfo.isFullMonth && (
                        <>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-400">Harga/Bulan</span>
                                <span className="font-semibold text-slate-200">Rp {formatRupiah(nominalPerBulan)}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-yellow-400/80">
                                    Proporsional ({proratedInfo.daysInPeriod}/{proratedInfo.daysInMonth} hari)
                                </span>
                                <span className="font-semibold text-yellow-300">
                                    Rp {formatRupiah(proratedInfo.proratedPrice)}
                                </span>
                            </div>
                        </>
                    )}
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-400">Subtotal</span>
                        <span className="font-semibold text-slate-200">Rp {formatRupiah(calc.subtotal)}</span>
                    </div>
                    {calc.discount > 0 && (
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-400">Diskon</span>
                            <span className="font-semibold text-red-400">-Rp {formatRupiah(calc.discount)}</span>
                        </div>
                    )}
                    {calc.ppnAmount > 0 && (
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-slate-400">PPN ({calc.ppnPercent}%)</span>
                            <span className="font-semibold text-cyan-400">+Rp {formatRupiah(calc.ppnAmount)}</span>
                        </div>
                    )}
                    <div className="border-t border-slate-700/50" />
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-slate-300 uppercase tracking-wider">Total Tagihan</span>
                        <span className="text-2xl font-extrabold text-cyan-400">Rp {formatRupiah(calc.grandTotal)}</span>
                    </div>
                </div>
            </div>

            {/* Kanan: Pembayaran */}
            <div className="lg:col-span-2 space-y-4">
                <div className="rounded-xl p-4 space-y-4" style={{ background: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Pembayaran</p>

                    {/* Metode */}
                    <div>
                        <label className="block text-xs text-slate-500 mb-2">Metode Pembayaran</label>
                        <div className="grid grid-cols-3 gap-2">
                            {PAYMENT_METHODS.map((method) => {
                                const isSelected = formData.metode_bayar === method.value;
                                const Icon = method.icon;
                                const hasError = errors.metode_bayar && !formData.metode_bayar;
                                return (
                                    <button
                                        key={method.value} type="button"
                                        onClick={() => {
                                            setFormData((prev) => ({ ...prev, metode_bayar: method.value }));
                                            setErrors((prev) => { const n = { ...prev }; delete n.metode_bayar; return n; });
                                        }}
                                        className="flex flex-col items-center gap-2 rounded-xl px-3 py-4 transition-all duration-200"
                                        style={{
                                            background: isSelected ? "rgba(0, 180, 216, 0.12)" : "rgba(15, 23, 42, 0.6)",
                                            border: hasError
                                                ? "1.5px solid rgba(239, 68, 68, 0.5)"
                                                : isSelected
                                                    ? "1.5px solid rgba(0, 180, 216, 0.5)"
                                                    : "1.5px solid rgba(51, 65, 85, 0.3)",
                                        }}
                                    >
                                        <Icon className="text-xl transition-colors duration-200" style={{ color: isSelected ? "#22d3ee" : "#475569" }} />
                                        <div className="text-center">
                                            <p className="text-xs font-bold transition-colors duration-200" style={{ color: isSelected ? "#67e8f9" : "#94a3b8" }}>{method.label}</p>
                                            <p className="text-[10px] mt-0.5 transition-colors duration-200" style={{ color: isSelected ? "rgba(103,232,249,0.6)" : "#475569" }}>{method.desc}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        <FieldError field="metode_bayar" />
                    </div>

                    {/* Nominal */}
                    <div>
                        <label className="block text-xs text-slate-500 mb-2">Nominal Pembayaran</label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-semibold">Rp</span>
                            <input
                                type="number" min="0" value={formData.nominal_bayar}
                                onChange={(e) => {
                                    setFormData((prev) => ({ ...prev, nominal_bayar: e.target.value }));
                                    setErrors((prev) => { const n = { ...prev }; delete n.nominal_bayar; return n; });
                                }}
                                disabled={loading} placeholder="Masukkan nominal"
                                className="w-full bg-slate-900/80 border border-slate-700 rounded-xl pl-10 pr-24 py-3.5 text-lg font-bold text-slate-200 text-right focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 disabled:opacity-50 transition"
                                style={errors.nominal_bayar ? { borderColor: "rgba(239,68,68,0.5)" } : {}}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    setFormData((prev) => ({ ...prev, nominal_bayar: calc.grandTotal }));
                                    setErrors((prev) => { const n = { ...prev }; delete n.nominal_bayar; return n; });
                                }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider text-cyan-400 hover:text-cyan-300 transition"
                                style={{ background: "rgba(0, 180, 216, 0.1)", border: "1px solid rgba(0, 180, 216, 0.2)" }}
                            >
                                Bayar Penuh
                            </button>
                        </div>
                        <FieldError field="nominal_bayar" />
                    </div>

                    {/* Status */}
                    {formData.nominal_bayar && Number(formData.nominal_bayar) > 0 && (
                        <div
                            className="rounded-xl p-3.5 space-y-2"
                            style={{
                                background: sisaPembayaran >= 0 ? "rgba(16, 185, 129, 0.06)" : "rgba(239, 68, 68, 0.06)",
                                border: sisaPembayaran >= 0 ? "1px solid rgba(16, 185, 129, 0.15)" : "1px solid rgba(239, 68, 68, 0.15)",
                            }}
                        >
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-400">Total Tagihan</span>
                                <span className="font-semibold text-slate-300">Rp {formatRupiah(calc.grandTotal)}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-400">Dibayar</span>
                                <span className="font-semibold text-cyan-400">Rp {formatRupiah(Number(formData.nominal_bayar))}</span>
                            </div>
                            <div className="border-t border-slate-700/50" />
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                    {sisaPembayaran >= 0 ? (sisaPembayaran === 0 ? "Lunas" : "Kembalian") : "Sisa Tagihan"}
                                </span>
                                <span
                                    className="text-lg font-extrabold"
                                    style={{ color: sisaPembayaran >= 0 ? (sisaPembayaran === 0 ? "#10b981" : "#22d3ee") : "#ef4444" }}
                                >
                                    {sisaPembayaran >= 0 ? "" : "-"}Rp {formatRupiah(Math.abs(sisaPembayaran))}
                                </span>
                            </div>
                        </div>
                    )}

                    {!formData.metode_bayar && !errors.metode_bayar && (
                        <div className="rounded-xl p-3 flex items-start gap-2.5" style={{ background: "rgba(234, 179, 8, 0.06)", border: "1px solid rgba(234, 179, 8, 0.15)" }}>
                            <FaCircleInfo className="text-yellow-500 text-xs mt-0.5 flex-shrink-0" />
                            <p className="text-[11px] text-yellow-400/80 leading-relaxed">Pilih metode pembayaran untuk melanjutkan.</p>
                        </div>
                    )}
                </div>

                <div className="rounded-xl p-3.5 flex items-start gap-3" style={{ background: "rgba(239, 68, 68, 0.06)", border: "1px solid rgba(239, 68, 68, 0.15)" }}>
                    <FaCircleXmark className="text-red-400 text-sm mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-400/80 leading-relaxed">
                        Invoice yang sudah diterbitkan <span className="font-semibold text-red-400">tidak dapat dibatalkan</span>. Pastikan semua data sudah benar.
                    </p>
                </div>
            </div>
        </div>
    );

    /* ══════════════════════════════════
       MAIN RENDER
       ══════════════════════════════════ */
    return (
        <ModalWraper isOpen={openModal} setIsOpen={setOpenModal} title="Terbitkan Invoice" subtitle={subtitle} icon={FaCalendarAlt} maxWidth={"max-w-5xl"}>
            <form onSubmit={handleSubmit} className="space-y-5">
                <div className="flex items-center gap-3">
                    {[1, 2].map((s) => (
                        <div key={s} className="flex items-center gap-2">
                            <div
                                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300"
                                style={{
                                    background: step >= s ? "linear-gradient(135deg, #0077B6, #00B4D8)" : "rgba(51, 65, 85, 0.4)",
                                    color: step >= s ? "#fff" : "#475569",
                                    boxShadow: step === s ? "0 0 12px rgba(0, 180, 216, 0.3)" : "none",
                                }}
                            >
                                {step > s ? <FaCircleCheck className="text-[10px]" /> : s}
                            </div>
                            <span className="text-xs font-semibold transition-colors duration-300 hidden sm:inline" style={{ color: step >= s ? "#67e8f9" : "#475569" }}>
                                {s === 1 ? "Tagihan" : "Pembayaran"}
                                {s === 2 && <span className="text-slate-600 font-normal ml-1">(opsional)</span>}
                            </span>
                            {s < 2 && <div className="w-8 h-px mx-1" style={{ background: step > s ? "rgba(0, 180, 216, 0.4)" : "rgba(51, 65, 85, 0.3)" }} />}
                        </div>
                    ))}
                </div>
                {step === 1 ? renderStep1() : renderStep2()}

                <div className="flex items-center justify-between gap-3 pt-2">
                    {step === 1 ? (
                        <p className="text-xs text-slate-500">
                            {periodInfo ? (
                                <>Periode: <span className="font-bold text-cyan-400">{periodInfo.label}</span>
                                    {proratedInfo && !proratedInfo.isFullMonth && (
                                        <span className="text-yellow-500/70 ml-1.5">({proratedInfo.daysInPeriod}/{proratedInfo.daysInMonth} hari)</span>
                                    )}
                                </>
                            ) : (
                                <span className="text-slate-600">Pilih tanggal mulai billing</span>
                            )}
                        </p>
                    ) : (
                        <button type="button" onClick={() => { setStep(1); setErrors({}); }} disabled={loading} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition disabled:opacity-50">
                            <FaArrowLeft className="text-xs" /> Kembali
                        </button>
                    )}

                    <div className="flex items-center gap-2.5 flex-wrap justify-end">
                        <button type="button" onClick={closeModal} disabled={loading} className="px-4 py-2.5 rounded-xl border border-slate-700 text-slate-300 hover:bg-slate-800 transition text-sm font-medium disabled:opacity-50">
                            Batal
                        </button>

                        {step === 1 && (
                            <>
                                <button
                                    type="button" disabled={!canAction} onClick={handleDirectPublish}
                                    className="px-4 py-2.5 rounded-xl font-semibold text-white transition-all duration-200 text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{
                                        background: canAction ? "linear-gradient(135deg, #475569, #64748b)" : "rgba(51, 65, 85, 0.4)",
                                        boxShadow: canAction ? "0 4px 12px rgba(100, 116, 139, 0.2)" : "none",
                                    }}
                                >
                                    {loading && <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                                    <FaBolt className="text-xs" /> Terbitkan
                                </button>
                                <button
                                    type="button" disabled={!canAction} onClick={() => { setStep(2); setErrors({}); }}
                                    className="px-5 py-2.5 rounded-xl font-semibold text-white transition-all duration-200 text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                                    style={{
                                        background: canAction ? "linear-gradient(135deg, #0077B6, #00B4D8)" : "rgba(51, 65, 85, 0.4)",
                                        boxShadow: canAction ? "0 4px 15px rgba(0, 180, 216, 0.2)" : "none",
                                    }}
                                >
                                    Terbitkan & Bayar <FaArrowLeft className="text-xs rotate-180" />
                                </button>
                            </>
                        )}

                        {step === 2 && (
                            <button
                                type="submit" disabled={loading}
                                className="px-5 py-2.5 rounded-xl font-semibold text-white transition-all duration-200 text-sm flex items-center gap-2 disabled:opacity-50"
                                style={{ background: "linear-gradient(135deg, #059669, #10B981)", boxShadow: "0 4px 15px rgba(16, 185, 129, 0.25)" }}
                            >
                                {loading ? (
                                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Menerbitkan...</>
                                ) : (
                                    <><FaCircleCheck className="text-xs" /> Terbitkan & Bayar</>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </ModalWraper>
    );
};